<template>
  <div :class="[classes, 'carousel']"
       tabindex="0"
       data-id="::carousel::"
       @mouseover="onMouseEnter"
       @mouseleave="onMouseLeave"
       @keyup="handleKeyboardEvent">
    <div v-if="slides && slides.length > 0"
         class="carousel__wrapper"
         :style="carouselStyle">
      <slide-transition v-for="(slide, index) in carouselSlides"
                        :key="index">
        <div v-show="slideIndex === index">
          <div :style="slideStyle"
               class="carousel__slide">
            <picture v-if="slide.imageEl">
              <source :srcset="slide.imageEl.src">
              <img class="carousel__image"
                   :width="slide.imageEl.width"
                   :height="slide.imageEl.height"
                   :src="`${slide.imageEl.src}
                    ${slide.imageEl.src && slide.imageEl.src.includes( '?' ) ? '&' : '?'}format=*`"
                   :srcSet="slide.imageEl.srcSet"
                   :alt="slide.imageEl.alt">
            </picture>

            <div class="carousel__container container">
              <div class="carousel__content"
                   :style="contentStyle">
                <!-- eslint-disable-next-line -->
                <div :class="['carousel__text-content', { 'carousel__heading-link': !slide.embedAsVideo }]">
                  <div v-if="heading"
                       class="carousel__main-heading">
                    {{ heading }}
                  </div>

                  <component
                    :is="headingSize"
                    class="carousel__heading"
                    :class="headingClasses"
                    :style="dropShadowStyle">
                    <template v-if="slide.embedAsVideo">
                      {{ slide.heading }}
                    </template>
                    <a v-else
                       :href="slide.linkUrl"
                       :title="slide.heading"
                       :target="slide.linkTarget"
                       class="carousel__heading-link">
                      {{ slide.heading }}
                    </a>
                  </component>

                  <sanitize-html
                    class="carousel__description"
                    :style="dropShadowStyle"
                    :dirty="slide.description">
                  </sanitize-html>

                  <div class="carousel__link-container">
                    <component
                      :is="slide.embedAsVideo ? 'button' : 'a'"
                      v-if="slide.linkText && slide.linkUrl"
                      :href="slide.embedAsVideo ? null : slide.linkUrl"
                      :class="['carousel__button btn', buttonClasses]"
                      :rel="slide.embedAsVideo ? null : 'nofollow'"
                      @click="openVideoModal(slide)">
                      <span>{{ slide.linkText }}</span>
                      <font-awesome-icon
                        :icon="['fas', slide.embedAsVideo ? 'play' : 'chevron-right']"
                        :class="['ml-2']"
                        aria-hidden="true">
                      </font-awesome-icon>
                    </component>
                  </div>
                </div>
                <!-- navigation -->
                <div
                  v-if="slides && slides.length > 1 && slides.length < maxNavDots"
                  class="carousel__navigator">
                  <font-awesome-icon
                    aria-hidden="true"
                    :icon="['fas', 'chevron-left']"
                    class="carousel__navigator-icon"
                    :tabindex="1"
                    @keyup="handleKeyboardEvent"
                    @click="clickedLeft"></font-awesome-icon>
                  <div
                    v-for="navIndex in slides.length"
                    :key="navIndex"
                    :tabindex="navIndex"
                    class="carousel__navigator-slide-dot"
                    :class="{
                      'carousel__navigator-slide-dot--selected': navIndex - 1 === slideIndex,
                    }"
                    @click="selectSlide(navIndex - 1)" />
                  <font-awesome-icon
                    aria-hidden="true"
                    :icon="['fas', 'chevron-right']"
                    class="carousel__navigator-icon"
                    :tabindex="2"
                    @keyup="handleKeyboardEvent"
                    @click="clickedRight"></font-awesome-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </slide-transition>
    </div>
    <progress-ring
      v-if="autoPlay && slides && slides.length > 1"
      ref="progressRing"
      aria-label="Pause carousel"
      :aria-pressed="`${!autoPlayCarousel}`"
      color="white"
      :class="'progress-ring--' + internalImageAlign"
      :radius="24"
      :thickness="3"
      :paused="progressedPaused || !autoPlayCarousel"
      :auto-play="autoPlayCarousel"
      :animation-duration="autoPlayDuration"
      pause-icon
      @clicked="togglePlayback">
    </progress-ring>

    <default-modal :id="instanceId"
                   body-class="hero-carousel-modal-body"
                   size="xl"
                   close-only>
      <template #heading>
        {{ modalTitle }}
      </template>
      <template #content>
        <embedded-video :src="modalLink"></embedded-video>
      </template>
    </default-modal>
  </div>
</template>

<script>
import { v4 as uuid } from "uuid";
import SlideTransition from "~/Components/Base/SlideTransition.vue";
import ProgressRing from "~/Components/Base/ProgressRing.vue";
import DefaultModal from "~/Components/Base/DefaultModal.vue";
import EmbeddedVideo from "~/Components/Common/EmbeddedVideo.vue";
import RecurringTimer from "~/Scripts/modules/timer";
import { KeyEventCodes } from "~/Scripts/mixins/common";
import SanitizeHtml from "./SanitizeHtml.vue";

export default {
  name: "HeroCarousel",
  components: {
    SlideTransition,
    ProgressRing,
    DefaultModal,
    EmbeddedVideo,
    SanitizeHtml,
  },
  mixins: [KeyEventCodes],
  props: {
    heading: {
      type: String,
      default: null,
    },
    headingStyle: {
      type: String,
      default: "bold",
    },
    headingSize: {
      type: String,
      default: "h2",
    },
    imageAlign: {
      type: String,
      default: "left",
    },
    buttonStyle: {
      type: String,
      default: "white",
    },
    slides: {
      type: Array,
      default: null,
    },
    buttonClass: {
      type: String,
      default: null,
    },
    gradientColor: {
      type: String,
      default: "#fff",
    },
    fullWidth: {
      type: Boolean,
      defautlt: true,
    },
    height: {
      type: String,
      default: "712px",
    },
    maxNavDots: {
      type: Number,
      default: 7,
    },
    autoPlay: {
      type: Boolean,
      default: true,
    },
    autoPlayDuration: {
      type: Number,
      default: 5000,
    },
  },
  data () {
    return {
      slideIndex: 0,
      intervalInstance: null,
      progressedPaused: false,
      mouseOverCarousel: false,
      autoPlayCarousel: false,
      modalTitle: null,
      modalLink: null,
    };
  },
  computed: {
    instanceId () {
      return uuid();
    },
    internalImageAlign () {
      return this.imageAlign.trim().toLowerCase();
    },
    currentSlideAlias () {
      return this.getSlideStateAlias( this.slideIndex );
    },
    currentSlide () {
      return this.slides[this.slideIndex];
    },
    slideStyle () {
      return {
        "background-image": `linear-gradient( to ${this.internalImageAlign},
         ${this.gradientColor} 40%, rgba( 0, 0, 0, 0 ) 70% )`,
        "min-height": this.height,
      };
    },
    classes () {
      const className = `carousel--${this.internalImageAlign}`;

      return {
        "carousel--full-width": this.fullWidth,
        [className]: true,
      };
    },
    headingClasses () {
      return {
        "carousel__heading--bold": this.headingStyle === "bold",
      };
    },
    buttonClasses () {
      return {
        "btn-accent-1": this.buttonStyle === "red",
        "btn-accent-2": this.buttonStyle === "white",
        "carousel__button--link": this.buttonStyle === "link",
      };
    },
    dropShadowStyle () {
      return {
        filter: `drop-shadow( 0 0 10px ${this.gradientColor} )`,
      };
    },
    contentStyle () {
      let result = null;

      if ( this.internalImageAlign === "left" ) {
        result = {
          "margin-left": "auto",
        };
      }

      return result;
    },
    carouselStyle () {
      return {
        "background-color": `${this.gradientColor}`,
      };
    },
    progressRing () {
      let result = null;

      if ( this.$refs.progressRing ) {
        result = this.$refs.progressRing;
      }

      return result;
    },
    carouselSlides () {
      let carouselSlides = null;

      if ( this.slides && this.slides.length ) {
        carouselSlides = this.slides.map( slide => {
          const mutableSlide = { ...slide, imageEl: undefined };

          if ( slide.image ) {
            const {
              url, width, height, alt,
            } = slide.image;

            if ( url ) {
              // preload images
              const image = new Image( width, height );
              image.src = url;
              image.srcset = `${url}?height=720 1x, ${url}?height=1440 2x`;
              image.alt = alt;
              mutableSlide.imageEl = image;
            }
          }
          return mutableSlide;
        } );
      }
      return carouselSlides;
    },
  },
  watch: {
    autoPlay: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.setInterval();
        } else if ( this.intervalInstance ) {
          this.clearInterval();
        }
      },
    },
  },
  mounted () {
    window.addEventListener( "focus", this.resetInterval );
  },
  created () {
    this.autoPlayCarousel = this.autoPlay;
  },
  destroyed () {
    window.removeEventListener( "focus", this.resetInterval );
  },
  methods: {
    openVideoModal ( slide ) {
      if ( slide.embedAsVideo ) {
        console.log( "Video modal - Slide data: ", slide );
        this.modalLink = slide.linkUrl;
        this.modalTitle = slide.heading;
        this.$bvModal.show( `${this.instanceId}` );
      }
    },
    clickedLeft () {
      this.slideLeft();
      this.reset();
    },
    clickedRight () {
      this.slideRight();
      this.reset();
    },
    slideLeft () {
      if ( this.slideIndex - 1 < 0 ) {
        this.selectSlide( this.slides.length - 1 );
      } else {
        this.selectSlide( this.slideIndex - 1 );
      }
    },
    slideRight () {
      if ( this.slideIndex + 1 > this.slides.length - 1 ) {
        this.selectSlide( 0 );
      } else {
        this.selectSlide( this.slideIndex + 1 );
      }
    },
    selectSlide ( index ) {
      this.slideIndex = index;
    },
    getSlideStateAlias ( index ) {
      return `slide_${index}`;
    },
    pause () {
      this.progressedPaused = true;

      if ( this.intervalInstance ) {
        this.intervalInstance.pause();
      }
    },
    resume () {
      this.progressedPaused = false;

      if ( this.intervalInstance ) {
        this.intervalInstance.resume();
      }
    },
    reset () {
      // Reset the autoplay interval and progress animation to keep sync.
      this.resetAnimation();
      this.resetInterval();
    },
    togglePlayback () {
      this.autoPlayCarousel = !this.autoPlayCarousel;
      ( this.autoPlayCarousel ? this.resume : this.pause )();
    },
    setInterval () {
      this.intervalInstance = new RecurringTimer( this.slideRight, this.autoPlayDuration );
    },
    clearInterval () {
      if ( this.intervalInstance ) {
        this.intervalInstance.clear();
      }
    },
    resetInterval () {
      this.clearInterval();
      this.setInterval();

      if ( this.mouseOverCarousel || this.autoPlayCarousel === false ) {
        this.pause();
      }
    },
    resetAnimation () {
      if ( this.progressRing ) {
        this.progressRing.resetAnimation();
      }
    },
    onMouseEnter () {
      this.mouseOverCarousel = true;

      if ( this.autoPlayCarousel ) {
        this.pause();
      }
    },
    onMouseLeave () {
      this.mouseOverCarousel = false;

      if ( this.autoPlayCarousel ) {
        this.resume();
      }
    },
    handleKeyboardEvent ( evt ) {
      if ( !evt ) {
        evt = window.event;
      } // for old IE compatible
      const keycode = evt.keyCode || evt.which; // also for cross-browser compatible
      const isCarouselEvent = document.activeElement?.getAttribute( "data-id" ) === "::carousel::";

      switch ( keycode ) {
        case this.keyboardEvent.keyCode.LEFT:
          this.clickedLeft();
          break;
        case this.keyboardEvent.keyCode.RIGHT:
          this.clickedRight();
          break;
        case this.keyboardEvent.keyCode.SPACE:
        case this.keyboardEvent.keyCode.ENTER: {
          // to prevent collision with progress ring MouseEvent
          if ( isCarouselEvent ) {
            this.togglePlayback();
          }
          break;
        }
        default:
          console.info( keycode );
          break;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.carousel {
  max-width: 1500px;
  margin: 0 auto;
  position: relative;
  &__wrapper {
    z-index: 2;
    position: relative;
  }

  &--full-width {
    max-width: inherit;
  }

  &__main-heading {
    color: #d71920;
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 1.25rem;

    @media (max-width: 992px) {
      font-size: 1.19rem;
      margin-bottom: 10px;
    }
  }

  &__slide {
    background-size: cover;
    display: flex;
    width: 100%;

    @media (max-width: 991px) {
      background-color: rgba(255, 255, 255, 0.7);
      background-image: none !important;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
    position: absolute;
  }

  @media (max-width: 991px) {
    &--right {
      .carousel__image {
        object-position: 75% 50%;
      }
    }

    &--left {
      .carousel__image {
        object-position: 25% 50%;
      }
    }
  }

  &__container {
    display: flex;
  }

  &__text-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    margin-bottom: 20px;

    @media (max-width: 992px) {
      margin-bottom: 10px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-basis: 40%;
    padding: 40px 0;

    @media (max-width: 992px) {
      flex-basis: 100%;
    }
  }

  &__heading {
    margin-bottom: 1.25rem;

    @media (max-width: 992px) {
      margin-bottom: 0.625rem;
    }

    &--normal {
      font-weight: 400;
    }

    &--bold {
      font-weight: 500;
    }

    &-link {
      text-decoration: none;
      color: #333;

      &:hover {
        text-decoration: none;
        color: #333;
      }
    }
  }

  &__description {
    font-size: 1.13rem;
    line-height: 1.5rem;
    margin-bottom: 1.25rem;

    @media (max-width: 992px) {
      font-size: 1.06rem;
      margin-bottom: 0.625rem;
    }

    h3 {
      margin-bottom: 1.25rem !important;
    }
  }

  &__button {
    text-decoration: none;
    text-transform: uppercase;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);

    /deep/ span {
      line-height: unset;
    }

    &:hover {
      text-decoration: none;
    }

    &:focus {
      outline: 1px auto #000;
    }

    &--red {
      color: #fff !important;
      background-color: #d71920;
      &:hover {
        border: 2px solid #fff;
      }
    }

    &--white {
      color: #d71920 !important;
      background-color: #fff;
      &:hover {
        border: 2px solid #d71920;
      }
    }

    &--link {
      color: #d71920 !important;
      box-shadow: none;
      font-weight: 400;
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__timer {
    position: absolute;
  }

  &__navigator {
    margin-top: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    &-icon {
      font-size: 1.13rem;
      &:first-child {
        margin-right: 20px;
      }

      &:last-child {
        margin-left: 20px;
      }

      &:hover {
        cursor: pointer;
      }

      &:focus {
        outline: #000 auto 1px;
      }
    }

    &-slide-dot {
      width: 20px;
      height: 20px;
      border: 2px solid lightgray;
      border-radius: 10px;
      margin: 0 10px;
      transition: background-color 0.2s ease-out, border-color 0.2s ease-out;
      &--selected {
        background-color: #333;
        border-color: #333;
      }

      &:focus {
        outline: #000 auto 1px;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  .progress-ring {
    position: absolute;
    margin: 10px;
    top: 0;
    z-index: 10000;
    cursor: pointer;
    &--right {
      right: 0;
    }

    &--left {
      left: 0;
    }
  }
}
</style>
